// Helpers para compatibilidade e normalização de templates

/**
 * Normaliza dados de templates para garantir compatibilidade
 * @param {Object|Array} templatesData - Dados dos templates da API
 * @returns {Array} Array normalizado de templates
 */
export const normalizeTemplatesData = (templatesData) => {
  if (!templatesData) return [];
  
  // Se já é um array, retorna como está
  if (Array.isArray(templatesData)) {
    return templatesData.map(template => normalizeTemplate(template));
  }
  
  // Se é um objeto com diferentes tipos (v2.0 API)
  if (typeof templatesData === 'object') {
    let allTemplates = [];
    
    // Combina templates de todos os tipos
    if (templatesData.whatsapp) {
      allTemplates = [...allTemplates, ...templatesData.whatsapp];
    }
    if (templatesData.email) {
      allTemplates = [...allTemplates, ...templatesData.email];
    }
    if (templatesData.webhook) {
      allTemplates = [...allTemplates, ...templatesData.webhook];
    }
    
    return allTemplates.map(template => normalizeTemplate(template));
  }
  
  return [];
};

/**
 * Normaliza um template individual
 * @param {Object} template - Template individual
 * @returns {Object} Template normalizado
 */
export const normalizeTemplate = (template) => {
  if (!template) return {};
  
  return {
    ...template,
    // Garante que template_type existe (retrocompatibilidade)
    template_type: template.template_type || 'whatsapp',
    // Normaliza a mensagem
    message: normalizeMessage(template.message),
    // Campos específicos de email
    email_subject: template.email_subject || '',
    email_html_content: template.email_html_content || '',
    email_from: template.email_from || '',
    // Campos específicos de webhook
    webhook_url: template.webhook_url || '',
    webhook_headers: template.webhook_headers || {},
    webhook_auth_token: template.webhook_auth_token || ''
  };
};

/**
 * Normaliza a estrutura da mensagem
 * @param {Object|String} message - Mensagem do template
 * @returns {Object} Mensagem normalizada
 */
export const normalizeMessage = (message) => {
  if (!message) return { text: '' };
  
  // Se já é um objeto, retorna como está
  if (typeof message === 'object') {
    return message;
  }
  
  // Se é string, converte para objeto
  if (typeof message === 'string') {
    try {
      return JSON.parse(message);
    } catch {
      return { text: message };
    }
  }
  
  return { text: '' };
};

/**
 * Filtra templates por tipo
 * @param {Array} templates - Array de templates
 * @param {String} type - Tipo de template ('whatsapp', 'email', 'webhook', 'all')
 * @returns {Array} Templates filtrados
 */
export const filterTemplatesByType = (templates, type = 'all') => {
  if (!Array.isArray(templates)) return [];
  
  if (type === 'all') return templates;
  
  return templates.filter(template => 
    (template.template_type || 'whatsapp') === type
  );
};

/**
 * Filtra templates por tipo e ação
 * @param {Array} templates - Array de templates
 * @param {String} type - Tipo de template
 * @param {String} action - Ação do template
 * @returns {Array} Templates filtrados
 */
export const filterTemplatesByTypeAndAction = (templates, type, action) => {
  if (!Array.isArray(templates)) return [];
  
  return templates.filter(template => {
    const templateType = template.template_type || 'whatsapp';
    const templateAction = template.action || 'generic';
    
    return templateType === type && templateAction === action;
  });
};

/**
 * Obtém a cor do badge baseada no tipo de template
 * @param {String} type - Tipo de template
 * @returns {String} Cor do badge
 */
export const getTemplateTypeBadgeColor = (type) => {
  const colors = {
    whatsapp: '#25D366',
    email: '#4285F4',
    webhook: '#FF6B6B'
  };
  return colors[type] || colors.whatsapp;
};

/**
 * Obtém o ícone baseado no tipo de template
 * @param {String} type - Tipo de template
 * @returns {String} Emoji do ícone
 */
export const getTemplateTypeIcon = (type) => {
  const icons = {
    whatsapp: '📱',
    email: '📧',
    webhook: '🔗'
  };
  return icons[type] || icons.whatsapp;
};

/**
 * Obtém o label baseado no tipo de template
 * @param {String} type - Tipo de template
 * @returns {String} Label do tipo
 */
export const getTemplateTypeLabel = (type) => {
  const labels = {
    whatsapp: 'WhatsApp',
    email: 'Email',
    webhook: 'Webhook'
  };
  return labels[type] || labels.whatsapp;
};

/**
 * Converte template para formato de criação/edição
 * @param {Object} template - Template a ser convertido
 * @param {String} templateType - Tipo de template
 * @returns {Object} Dados formatados para o formulário
 */
export const convertTemplateForForm = (template, templateType = 'whatsapp') => {
  if (!template) return {};
  
  const baseData = {
    name: template.name || '',
    action: template.action || (templateType === 'email' ? 'email' : 'generic'),
    template_type: templateType
  };
  
  if (templateType === 'whatsapp') {
    return {
      ...baseData,
      message_type: getWhatsAppMessageType(template),
      text_message: getWhatsAppTextMessage(template)
    };
  }
  
  if (templateType === 'email') {
    return {
      ...baseData,
      email_subject: template.email_subject || '',
      email_html_content: template.email_html_content || '',
      email_from: template.email_from || ''
    };
  }
  
  if (templateType === 'webhook') {
    return {
      ...baseData,
      webhook_url: template.webhook_url || '',
      webhook_headers: template.webhook_headers || {},
      webhook_auth_token: template.webhook_auth_token || ''
    };
  }
  
  return baseData;
};

/**
 * Determina o tipo de mensagem WhatsApp
 * @param {Object} template - Template WhatsApp
 * @returns {String} Tipo da mensagem
 */
export const getWhatsAppMessageType = (template) => {
  if (!template.message) return 'text';
  
  try {
    const message = typeof template.message === 'string' 
      ? JSON.parse(template.message) 
      : template.message;
    
    if (message.image) return 'image';
    if (message.audio) return 'audio';
    if (message.document) return 'document';
    return 'text';
  } catch {
    return 'text';
  }
};

/**
 * Extrai o texto da mensagem WhatsApp
 * @param {Object} template - Template WhatsApp
 * @returns {String} Texto da mensagem
 */
export const getWhatsAppTextMessage = (template) => {
  if (!template.message) return '';
  
  try {
    const message = typeof template.message === 'string' 
      ? JSON.parse(template.message) 
      : template.message;
    
    return message.text || '';
  } catch {
    return '';
  }
};

/**
 * Valida se os dados do template estão corretos
 * @param {Object} templateData - Dados do template
 * @param {String} templateType - Tipo de template
 * @returns {Object} { isValid: boolean, errors: array }
 */
export const validateTemplateData = (templateData, templateType) => {
  const errors = [];
  
  // Validações básicas
  if (!templateData.name?.trim()) {
    errors.push('Nome do template é obrigatório');
  }
  
  if (!templateData.action?.trim()) {
    errors.push('Ação do template é obrigatória');
  }
  
  // Validações específicas por tipo
  if (templateType === 'email') {
    if (!templateData.email_subject?.trim()) {
      errors.push('Assunto do email é obrigatório');
    }
    
    if (!templateData.email_html_content?.trim()) {
      errors.push('Conteúdo HTML é obrigatório');
    }
    
    if (!templateData.email_from?.trim()) {
      errors.push('Email do remetente é obrigatório');
    }
    
    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (templateData.email_from && !emailRegex.test(templateData.email_from)) {
      errors.push('Email do remetente é inválido');
    }
  }
  
  if (templateType === 'webhook') {
    if (!templateData.webhook_url?.trim()) {
      errors.push('URL do webhook é obrigatória');
    }
    
    // Validação HTTPS
    if (templateData.webhook_url && !templateData.webhook_url.startsWith('https://')) {
      errors.push('URL do webhook deve usar HTTPS');
    }
  }
  
  if (templateType === 'whatsapp') {
    if (templateData.message_type === 'text' && !templateData.text_message?.trim()) {
      errors.push('Mensagem de texto é obrigatória');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  normalizeTemplatesData,
  normalizeTemplate,
  normalizeMessage,
  filterTemplatesByType,
  filterTemplatesByTypeAndAction,
  getTemplateTypeBadgeColor,
  getTemplateTypeIcon,
  getTemplateTypeLabel,
  convertTemplateForForm,
  getWhatsAppMessageType,
  getWhatsAppTextMessage,
  validateTemplateData
};